/**
 * Taken from Airbnb react-dates v20.2.5
 */

/* eslint-disable react/no-did-update-set-state */

import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { isInclusivelyAfterDay } from 'react-dates';

import momentPropTypes from 'react-moment-proptypes';
import { forbidExtraProps, nonNegativeInteger } from 'airbnb-prop-types';
import openDirectionShape from './shapes/OpenDirectionShape';

import { DateRangePickerInputPhrases } from './defaultPhrases';
import getPhrasePropTypes from './utils/getPhrasePropTypes';

import isBeforeDay from './utils/isBeforeDay';

import DateRangePickerInput from './DateRangePickerInput';

import IconPositionShape from './shapes/IconPositionShape';
import DisabledShape from './shapes/DisabledShape';

import toMomentObject from './utils/toMomentObject';
import toLocalizedDateString from './utils/toLocalizedDateString';
import DateValidator from './utils/DateValidator';

import {
  START_DATE,
  END_DATE,
  ICON_BEFORE_POSITION,
  OPEN_DOWN
} from './constants';

const propTypes = forbidExtraProps({
  children: PropTypes.node,

  startDate: momentPropTypes.momentObj,
  startDateId: PropTypes.string,
  startDatePlaceholderText: PropTypes.string,
  isStartDateFocused: PropTypes.bool,
  startDateAriaLabel: PropTypes.string,
  startDateError: PropTypes.string,

  endDate: momentPropTypes.momentObj,
  endDateId: PropTypes.string,
  endDatePlaceholderText: PropTypes.string,
  isEndDateFocused: PropTypes.bool,
  endDateAriaLabel: PropTypes.string,
  endDateError: PropTypes.string,

  screenReaderMessage: PropTypes.string,
  showClearDates: PropTypes.bool,
  showCaret: PropTypes.bool,
  showDefaultInputIcon: PropTypes.bool,
  inputIconPosition: IconPositionShape,
  disabled: DisabledShape,
  required: PropTypes.bool,
  readOnly: PropTypes.bool,
  openDirection: openDirectionShape,
  noBorder: PropTypes.bool,
  block: PropTypes.bool,
  small: PropTypes.bool,
  regular: PropTypes.bool,
  verticalSpacing: nonNegativeInteger,

  keepOpenOnDateSelect: PropTypes.bool,
  reopenPickerOnClearDates: PropTypes.bool,
  withFullScreenPortal: PropTypes.bool,
  minimumNights: nonNegativeInteger,
  isOutsideRange: PropTypes.func,
  isDayBlocked: PropTypes.func,
  displayFormat: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),

  onFocusChange: PropTypes.func,
  onClose: PropTypes.func,
  onDatesChange: PropTypes.func,
  onKeyDownArrowDown: PropTypes.func,
  onKeyDownQuestionMark: PropTypes.func,

  customInputIcon: PropTypes.node,
  customArrowIcon: PropTypes.node,
  customCloseIcon: PropTypes.node,

  // accessibility
  isFocused: PropTypes.bool,

  // i18n
  phrases: PropTypes.shape(getPhrasePropTypes(DateRangePickerInputPhrases)),

  isRTL: PropTypes.bool
});

const defaultProps = {
  children: null,

  startDate: null,
  startDateId: START_DATE,
  startDatePlaceholderText: 'Start Date',
  isStartDateFocused: false,
  startDateAriaLabel: undefined,

  endDate: null,
  endDateId: END_DATE,
  endDatePlaceholderText: 'End Date',
  isEndDateFocused: false,
  endDateAriaLabel: undefined,

  screenReaderMessage: '',
  showClearDates: false,
  showCaret: false,
  showDefaultInputIcon: false,
  inputIconPosition: ICON_BEFORE_POSITION,
  disabled: false,
  required: false,
  readOnly: false,
  openDirection: OPEN_DOWN,
  noBorder: false,
  block: false,
  small: false,
  regular: false,
  verticalSpacing: undefined,

  keepOpenOnDateSelect: false,
  reopenPickerOnClearDates: false,
  withFullScreenPortal: false,
  minimumNights: 1,
  isOutsideRange: (day) => !isInclusivelyAfterDay(day, moment()),
  isDayBlocked: () => false,
  displayFormat: () => moment.localeData().longDateFormat('L'),

  onFocusChange() { },
  onClose() { },
  onDatesChange() { },
  onKeyDownArrowDown() { },
  onKeyDownQuestionMark() { },

  customInputIcon: null,
  customArrowIcon: null,
  customCloseIcon: null,

  // accessibility
  isFocused: false,

  // i18n
  phrases: DateRangePickerInputPhrases,

  isRTL: false
};

export default class DateRangePickerInputController extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      startDateError: props.startDateError || null,
      endDateError: props.endDateError || null,
      startDisplayErrorDate: null,
      endDisplayErrorDate: null
    };

    this.onClearFocus = this.onClearFocus.bind(this);
    this.onStartDateChange = this.onStartDateChange.bind(this);
    this.onStartDateFocus = this.onStartDateFocus.bind(this);
    this.onEndDateChange = this.onEndDateChange.bind(this);
    this.onEndDateFocus = this.onEndDateFocus.bind(this);
    this.clearDates = this.clearDates.bind(this);
  }

  componentDidUpdate(prevProps) {
    const { startDate, endDate, startDateError, endDateError } = this.props;

    if (startDate && startDate !== prevProps.startDate) {
      this.setState({
        startDateError: null,
        startDisplayErrorDate: null
      });
    }

    if (endDate && endDate !== prevProps.endDate) {
      this.setState({
        endDateError: null,
        endDisplayErrorDate: null
      });
    }


    if (startDateError && !prevProps.startDateError && !this.state.startDateError) {
      this.setState({ startDateError });
    }

    if (endDateError && !prevProps.endDateError && !this.state.endDateError) {
      this.setState({ endDateError });
    }
  }

  onClearFocus() {
    const {
      onFocusChange,
      onClose,
      startDate,
      endDate
    } = this.props;

    onFocusChange(null);
    onClose({ startDate, endDate });
  }

  onEndDateChange(endDateString) {
    const {
      startDate,
      isOutsideRange,
      isDayBlocked,
      minimumNights,
      keepOpenOnDateSelect,
      onDatesChange
    } = this.props;

    // If field is empty, reset error states and call onDatesChange with the
    // empty value so the input value will clear correctly
    if (!endDateString) {
      this.setState({
        endDateError: null,
        endDisplayErrorDate: null
      });

      onDatesChange({
        startDate,
        endDate: null
      });

      return;
    }

    const endDate = toMomentObject(endDateString, this.getDisplayFormat());

    const isEndDateValid = endDate
      && !isOutsideRange(endDate)
      && !isDayBlocked(endDate)
      && !(startDate && isBeforeDay(endDate, startDate.clone().add(minimumNights, 'days')));
    if (isEndDateValid) {
      onDatesChange({ startDate, endDate });
      this.setState({
        endDateError: null,
        endDisplayErrorDate: null
      });

      if (!keepOpenOnDateSelect) this.onClearFocus();
    }
    else if (endDateString.length === this.getDisplayFormat().length) {
      onDatesChange({
        startDate,
        endDate: null
      });

      this.setState({
        endDisplayErrorDate: null
      });

      if (!keepOpenOnDateSelect) {
        this.onClearFocus();
      }
    }
  }

  onEndDateFocus() {
    const {
      startDate,
      onFocusChange,
      withFullScreenPortal,
      disabled
    } = this.props;

    if (!startDate && withFullScreenPortal && (!disabled || disabled === END_DATE)) {
      // When the datepicker is full screen, we never want to focus the end date first
      // because there's no indication that that is the case once the datepicker is open and it
      // might confuse the user
      onFocusChange(START_DATE);
    }
    else if (!disabled || disabled === START_DATE) {
      onFocusChange(END_DATE);
    }
  }

  onEndDateBlur = (inputValue) => {
    const {
      startDate,
      endDate,
      onDatesChange,
      isOutsideRange,
      isDayBlocked,
      minimumNights
    } = this.props;

    if (!inputValue) {
      // Don't fire onChange is there's no change
      return;
    }

    // If empty, reset validation and clear date
    // if (!inputValue) {
    //   this.setState({
    //     endDateError: null,
    //     endDisplayErrorDate: null
    //   });

    //   return;
    // }

    const parsed = DateValidator({
      input: inputValue,
      format: this.getDisplayFormat()
    });

    // If day hasn't changed, return. User is likely using the calendar picker
    if (parsed.isValidMoment && moment(endDate).isSame(parsed.moment, 'day')) {
      this.setState({
        endDateError: null,
        endDisplayErrorDate: null
      });
      return;
    }

    const isEndDateBeforeStartDate = startDate
      && isBeforeDay(parsed.moment, startDate.clone().add(minimumNights, 'days'));
    const isEndDateValid = parsed.isValidMoment
      && !isOutsideRange(parsed.moment)
      && !isDayBlocked(parsed.moment)
      && !(isEndDateBeforeStartDate);

    // If the user input is not valid
    if (!isEndDateValid) {
      let errorMessage = parsed.errorMessage || 'is not available.';

      if (isEndDateBeforeStartDate) {
        errorMessage = 'is not valid.'
      }

      this.setState({
        endDateError: errorMessage
      });

      onDatesChange({
        startDate,
        endDate: null
      });

      if (parsed.isValidMoment) {
        this.setState({
          endDisplayErrorDate: this.getDateString(parsed.moment)
        });
      }
    }
    // else it's valid
    else {
      onDatesChange({
        startDate,
        endDate: parsed.moment.startOf('day')
      });

      this.setState({
        endDateError: null
      });

      // if (!keepOpenOnDateSelect) this.onClearFocus();
    }
  }

  onStartDateChange(startDateString) {
    let { endDate } = this.props;
    const {
      isOutsideRange,
      isDayBlocked,
      minimumNights,
      onDatesChange,
      onFocusChange,
      disabled
    } = this.props;

    if (!startDateString) {
      this.setState({
        startDateError: null,
        startDisplayErrorDate: null
      });

      onDatesChange({
        startDate: null,
        endDate
      });

      return;
    }

    const startDate = toMomentObject(startDateString, this.getDisplayFormat());
    const isEndDateBeforeStartDate = startDate
      && isBeforeDay(endDate, startDate.clone().add(minimumNights, 'days'));
    const isStartDateValid = startDate
      && !isOutsideRange(startDate)
      && !isDayBlocked(startDate)
      && !(disabled === END_DATE && isEndDateBeforeStartDate);

    if (isStartDateValid) {
      if (isEndDateBeforeStartDate) {
        endDate = null;
      }
      onDatesChange({ startDate, endDate });

      this.setState({
        startDateError: null,
        startDisplayErrorDate: null
      });

      onFocusChange(END_DATE);
    }

    else if (startDateString.length === this.getDisplayFormat().length) {
      this.setState({
        startDisplayErrorDate: startDateString,
        startDateError: 'is not available.'
      }, () => {
        onDatesChange({
          startDate: null,
          endDate
        });
        onFocusChange(END_DATE);
      });
    }
  }

  onStartDateFocus() {
    const { disabled, onFocusChange } = this.props;

    if (!disabled || disabled === END_DATE) {
      onFocusChange(START_DATE);
    }
  }

  onStartDateBlur = (inputValue) => {
    const {
      startDate,
      endDate,
      onDatesChange,
      isOutsideRange,
      isDayBlocked,
      onFocusChange,
      minimumNights,
      disabled
    } = this.props;

    if (!inputValue) {
      // Don't fire onChange if there's no change
      return;
    }

    // Otherwise validate input
    const parsed = DateValidator({
      input: inputValue,
      format: this.getDisplayFormat()
    });

    // If day hasn't changed, return - likely user using the calendar picker
    if (parsed.isValid && moment(startDate).isSame(parsed.moment, 'day')) {
      return;
    }

    const isEndDateBeforeStartDate = parsed.isValidMoment
      && isBeforeDay(endDate, parsed.moment.clone().add(minimumNights, 'days'));
    const isStartDateValid = parsed.isValidMoment
      && !isOutsideRange(parsed.moment)
      && !isDayBlocked(parsed.moment)
      && !(disabled === END_DATE && isEndDateBeforeStartDate);

    // Start date is not valid
    if (!isStartDateValid) {
      let errorMessage = parsed.errorMessage || 'is not available.';

      if (isEndDateBeforeStartDate) {
        errorMessage = 'is not valid.';
      }

      this.setState({
        startDateError: errorMessage
      });

      onDatesChange({
        startDate: null,
        endDate
      });

      if (parsed.isValidMoment) {
        this.setState({
          startDisplayErrorDate: this.getDateString(parsed.moment)
        });
      }
    }

    // else the start date is valid
    else {
      const newStartDate = parsed.moment.startOf('day');
      // If the new start date makes the range invalid, we don't want to
      // call onDatesChange with the endDate ... we'll set it to null
      if (endDate && this.isRangeValid(newStartDate, endDate)) {
        onDatesChange({
          startDate: newStartDate,
          endDate
        });
      }
      else {
        onDatesChange({
          startDate: newStartDate,
          endDate: null
        });
      }

      this.setState({
        startDateError: null,
        startDisplayErrorDate: null
      });
    }

    onFocusChange(END_DATE);
  }

  getDisplayFormat() {
    const { displayFormat } = this.props;
    return typeof displayFormat === 'string' ? displayFormat : displayFormat();
  }

  getDateString(date) {
    const displayFormat = this.getDisplayFormat();
    if (date && displayFormat) {
      return date && date.format(displayFormat);
    }
    return toLocalizedDateString(date);
  }

  isRangeValid(startDate, endDate) {
    const { minimumNights } = this.props;
    // false if range is not complete
    if (!startDate || !endDate) {
      return false;
    }
    // false if end is before start or endDate is before startDate + minimumNights
    const minimumEndDate = startDate.clone().add(minimumNights, 'days');
    if (endDate.isBefore(minimumEndDate)) {
      return false;
    }
    return true;
  }

  clearDates() {
    const { onDatesChange, reopenPickerOnClearDates, onFocusChange } = this.props;
    onDatesChange({ startDate: null, endDate: null });

    this.setState({
      startDateError: null,
      endDateError: null,
      startDisplayErrorDate: null,
      endDisplayErrorDate: null
    })
    if (reopenPickerOnClearDates) {
      onFocusChange(START_DATE);
    }
  }

  render() {
    const {
      children,
      startDate,
      startDateId,
      startDatePlaceholderText,
      isStartDateFocused,
      startDateAriaLabel,
      endDate,
      endDateId,
      endDatePlaceholderText,
      endDateAriaLabel,
      isEndDateFocused,
      screenReaderMessage,
      showClearDates,
      showCaret,
      showDefaultInputIcon,
      inputIconPosition,
      customInputIcon,
      customArrowIcon,
      customCloseIcon,
      disabled,
      required,
      readOnly,
      openDirection,
      isFocused,
      phrases,
      onKeyDownArrowDown,
      onKeyDownQuestionMark,
      isRTL,
      noBorder,
      block,
      small,
      regular,
      verticalSpacing
    } = this.props;

    const {
      startDateError,
      endDateError,
      startDisplayErrorDate,
      endDisplayErrorDate
    } = this.state;

    const startDateString = this.getDateString(startDate) || startDisplayErrorDate || '';
    const endDateString = this.getDateString(endDate) || endDisplayErrorDate || '';

    return (
      <DateRangePickerInput
        startDate={startDateString}
        startDateId={startDateId}
        startDatePlaceholderText={startDatePlaceholderText}
        isStartDateFocused={isStartDateFocused}
        isStartDateInvalid={!!startDateError}
        startDateAriaLabel={startDateAriaLabel}
        endDate={endDateString}
        endDateId={endDateId}
        endDatePlaceholderText={endDatePlaceholderText}
        isEndDateFocused={isEndDateFocused}
        isEndDateInvalid={!!endDateError}
        endDateAriaLabel={endDateAriaLabel}
        startDateError={startDateError}
        endDateError={endDateError}
        isFocused={isFocused}
        disabled={disabled}
        required={required}
        readOnly={readOnly}
        openDirection={openDirection}
        showCaret={showCaret}
        showDefaultInputIcon={showDefaultInputIcon}
        inputIconPosition={inputIconPosition}
        customInputIcon={customInputIcon}
        customArrowIcon={customArrowIcon}
        customCloseIcon={customCloseIcon}
        phrases={phrases}
        onStartDateChange={this.onStartDateChange}
        onStartDateFocus={this.onStartDateFocus}
        onStartDateBlur={this.onStartDateBlur}
        onStartDateShiftTab={this.onClearFocus}
        onEndDateChange={this.onEndDateChange}
        onEndDateFocus={this.onEndDateFocus}
        onEndDateBlur={this.onEndDateBlur}
        showClearDates={showClearDates}
        onClearDates={this.clearDates}
        screenReaderMessage={screenReaderMessage}
        onKeyDownArrowDown={onKeyDownArrowDown}
        onKeyDownQuestionMark={onKeyDownQuestionMark}
        isRTL={isRTL}
        noBorder={noBorder}
        block={block}
        small={small}
        regular={regular}
        verticalSpacing={verticalSpacing} >
        {children}
      </DateRangePickerInput>
    );
  }
}

DateRangePickerInputController.propTypes = propTypes;
DateRangePickerInputController.defaultProps = defaultProps;
